import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { SurfaceLine3D } from 'features/assetViz/objects/decor/SurfaceLine3D'
import { useSurfaceProps } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import { Vec3 } from 'features/assetViz/utils/helpers'
import React, { useMemo } from 'react'
import {
  GeckoAssetModel,
  GeckoComponentModelType,
  GeckoComponentPath,
  LineDirectionX,
  LineDirectionY,
} from 'types'

import { getCompassAnglePositionData } from './helpers'

/**
 * Compass component that places vertical lines at angle delta intervals from the starting orientation.
 * Assumes that the component covers a full rotation of 360 degrees.
 *
 * @param assetModel the model of the asset that the compass is being applied on
 * @param componentModel the model of the component that the compass would be applied on - currently forced to be a vertical tank shell
 * @param radial boolean for expecting a radial compass which centers html tags
 *               rather than bottom positioning them
 * @param angleDeltaDegrees override the default 90 degree intervals with a different angle. Must be a factor of 90.
 *                   Any angle that isn't a cardinal direction will be displayed as degrees.
 */

export const CompassLines: React.FC<{
  assetModel: GeckoAssetModel
  getRotationPosition: (rotation: number) => Vec3
  radial?: boolean
  angleDeltaDegrees?: 1 | 2 | 3 | 5 | 6 | 9 | 10 | 15 | 18 | 30 | 45 | 90
}> = ({ assetModel, getRotationPosition, angleDeltaDegrees = 90 }) => {
  const { showCompass } = useAssetVizInternal()
  const { componentModel } = useSurfaceProps()

  const showThisCompass = React.useMemo(() => {
    if (showCompass === true || showCompass === false) return showCompass
    return componentModel && showCompass(componentModel)
  }, [showCompass, componentModel])

  const compassOrientation = useMemo(() => {
    return componentModel?.type === GeckoComponentModelType.VerticalTankShell ||
      componentModel?.type === GeckoComponentModelType.HorizontalTankShell
      ? componentModel?.compassOrientation
      : assetModel.data.compass_orientation
  }, [assetModel.data, componentModel])

  const { compassPositions, compassAngularPos } = useMemo(() => {
    if (compassOrientation === undefined) {
      return { compassPositions: [], compassAngularPos: [] }
    }
    return getCompassAnglePositionData(
      compassOrientation,
      getRotationPosition,
      assetModel.data.plant_north_compass_orientation,
      angleDeltaDegrees,
      0 // No angle offset for vertical shell compass lines
    )
  }, [
    compassOrientation,
    getRotationPosition,
    angleDeltaDegrees,
    assetModel.data.plant_north_compass_orientation,
  ])
  //----------------------------------------------------------------------------
  // Path for the compass lines for Vertical Tank Shells
  const pathCoords = React.useMemo(() => {
    //assume its a vertical tank shell right here
    if (!componentModel) {
      return undefined
    }
    const componentBounds = componentModel.componentBounds
    const outXPositions = compassAngularPos.map((rotation) => {
      const weightedAngle = -rotation / 360
      return componentBounds.start.x
        .minus(componentBounds.end.x)
        .scale(weightedAngle)
        .plus(componentBounds.start.x)
    })
    return outXPositions.map((x) => {
      return GeckoComponentPath.CreateFromValues(
        componentModel.id,
        [
          { x: x.num, y: componentBounds.start.y.num },
          { x: x.num, y: componentBounds.end.y.num },
        ],
        x.unit,
        componentBounds.y.unit,
        [LineDirectionX.None],
        [LineDirectionY.Up]
      )
    })
  }, [componentModel, compassAngularPos])

  return !pathCoords || !showThisCompass
    ? null
    : compassPositions.map((_, i) => {
        return <SurfaceLine3D key={`compass_line_${i}`} path={pathCoords[i]} />
      })
}
